import React, { useState, useEffect } from 'react'
import ms from 'ms'
import Cookies from 'js-cookie'
import { hooks as coreHooks } from '@adc/ui-core'
import * as blueprint from '@adc/blueprint'
import configment from '@adc/configment'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import styles from './styles.scss'
import {
  runSurveyMonkeyScript,
  surveyCookieName,
} from '../auctionApplicationProvider/getSurveyMonkeyScript'
import { useApplicationContext } from '../auctionApplication/context'
import { useApplicationSurvey } from '../auctionApplication/applicationSurvey'
import { enums } from '@adc/sdk'
const cx = classnames.bind(styles)

const SurveyButton = ({
  isCapabilityEnabled = true,
  viewingFrom = enums.CurrentTargetAudience.Buyer,
}) => {
  const { data, actions } = coreHooks.useWebApplicationNudges()
  const { user } = useApplicationContext()

  // WHY: When Survey monkey iframe loads, it stores a cookie in our browser to prevent it from loading again based on their own logic
  // If we want to have control of when the survey loads via our own tools we need to manually remove this cookie whenever the page loads
  if (Cookies.get(surveyCookieName)) {
    Cookies.remove(surveyCookieName)
  }

  const onClick = () => {
    runSurveyMonkeyScript(onSurveyClose, viewingFrom)
    setIsSurveyMonkeyLoaded(true)
    setHasSurveyBeenOpened(true)
    if (!user.logged_in) {
      actions.nudge('feedback_button_prompt')
    }
  }

  const onSurveyClose = async () => {
    if (user.logged_in) {
      try {
        blueprint.user.providers.setUserMarketingMilestones()
      } catch (e) {
        console.error('Failed to save survey feedback date', e)
      }
    }
  }

  const [isSurveyMonkeyLoaded, setIsSurveyMonkeyLoaded] = useState(true)
  const [hasSurveyBeenOpened, setHasSurveyBeenOpened] = useState(false)
  const [isFetchingData, setIsFetchingData] = useState(true)
  const [isSurveyExpired, setIsSurveyExpired] = useState(false)

  useEffect(() => {
    if (isFetchingData) return

    if (!hasSurveyBeenOpened) {
      setIsSurveyMonkeyLoaded(!isSurveyExpired)
    }
  }, [hasSurveyBeenOpened, isSurveyExpired, isFetchingData])

  const checkIsExpired = lastDateString => {
    if (!lastDateString) return true
    const nudgesDurations = configment.get('NUDGE_RECURRENCE_DURATIONS', {})
    const currentDate = new Date()
    const calculatedNextSurvey = new Date(lastDateString)
    calculatedNextSurvey.setMilliseconds(
      calculatedNextSurvey.getMilliseconds() +
        ms(nudgesDurations.feedback_button_prompt || '5m')
    )
    return currentDate > calculatedNextSurvey
  }

  const isStorageDataLoaded = !(
    data.active.size === data.inactive.size && data.active.size === 0
  )

  const feebackNudgeInactive = data.inactive.has('feedback_button_prompt')

  useEffect(() => {
    const getUserMarketingMilestones = async () => {
      try {
        const result =
          await blueprint.user.providers.getUserMarketingMilestones()
        if (result?.marketing_milestones) {
          setIsSurveyExpired(
            checkIsExpired(
              result?.marketing_milestones?.survey_feedback_submitted_date
            )
          )
        } else {
          setIsSurveyExpired(!feebackNudgeInactive)
        }
        setIsFetchingData(false)
      } catch (e) {
        setIsSurveyExpired(!feebackNudgeInactive)
      } finally {
        setIsFetchingData(false)
      }
    }
    if (user.logged_in) {
      if (!hasSurveyBeenOpened && isStorageDataLoaded) {
        getUserMarketingMilestones()
      }
    } else {
      if (!hasSurveyBeenOpened && isStorageDataLoaded) {
        setIsFetchingData(false)
        setIsSurveyExpired(!feebackNudgeInactive)
      }
    }
  }, [
    hasSurveyBeenOpened,
    isStorageDataLoaded,
    user.logged_in,
    feebackNudgeInactive,
  ])

  const { isSurveyVisible } = useApplicationSurvey()
  const isMounted = coreHooks.useIsMounted()

  if (
    !isMounted ||
    !isCapabilityEnabled ||
    isSurveyMonkeyLoaded ||
    !isSurveyVisible ||
    isFetchingData ||
    !isStorageDataLoaded
  )
    return null

  return (
    <div
      data-elm-id='survey-monkey-button'
      className={cx('survey-monkey-button')}
      onClick={onClick}
    >
      Feedback
    </div>
  )
}

SurveyButton.propTypes = {
  isCapabilityEnabled: PropTypes.bool,
  viewingFrom: PropTypes.oneOf(Object.values(enums.CurrentTargetAudience)),
}

export default SurveyButton
